"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { useCart } from "../../CartContent"
import { API_BASE_URL } from "../../config"
import { CheckCircle } from "lucide-react" // For the notification icon
import MenuItemSkeleton from "./MenuItemSkeleton" // Import the new skeleton component

const MenuSection = ({ title, heading, items = [], loading = false }) => {
  const { addToCart } = useCart()
  const [loadedImages, setLoadedImages] = useState({})
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState("")

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }))
  }

  const handleAddToCartClick = (item) => {
    const { id, item_data = {} } = item
    const variations = item_data.variations || []
    const firstVariation = variations[0]
    const price = firstVariation?.item_variation_data?.price_money?.amount
      ? (firstVariation.item_variation_data.price_money.amount / 100).toFixed(2)
      : "0.00"

    addToCart({
      id: firstVariation?.id || id, // Use variation ID for checkout
      itemId: id, // Keep original item ID for reference
      name: item_data.name,
      price: Number.parseFloat(price),
      image: item_data.image_ids?.[0]
        ? `${API_BASE_URL}/api/image/${item_data.image_ids[0]}`
        : "https://via.placeholder.com/300x240?text=No+Image",
      quantity: 1,
      selectedModifiers: {}, // No modifiers selected initially from menu page
      specialNote: null,
    })

    setNotificationMessage(`${item_data.name} added to cart!`)
    setShowNotification(true)
    setTimeout(() => {
      setShowNotification(false)
      setNotificationMessage("")
    }, 3000) // Notification disappears after 3 seconds
  }

  return (
    <div className="w-full px-4 sm:px-6 md:px-8 lg:px-16 py-12 md:py-20 bg-black">
      <div className="w-full max-w-7xl mx-auto flex flex-col items-center gap-12 md:gap-20">
        {/* Title and Heading */}
        <div className="w-full max-w-2xl flex flex-col items-center gap-4 text-center">
          <h2 className="text-orange-400 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight md:leading-[57.6px]">
            {title}
          </h2>
          <p className="text-white text-base md:text-lg leading-relaxed">{heading}</p>
        </div>

        {/* Menu Items */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 lg:gap-10 w-full">
            {Array.from({ length: 8 }).map((_, index) => (
              <MenuItemSkeleton key={index} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-white text-center text-lg md:text-xl font-medium">
            No items found for this category. Check back soon!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 lg:gap-10 w-full">
            {items.map((item) => {
              const { id, item_data = {} } = item

              const name = item_data.name || ""
              const description = item_data.description_plaintext || item_data.description || ""
              const variations = item_data.variations || []
              const modifierListInfo = item_data.modifier_list_info || []
              const imageId = item_data.image_ids?.[0]
              const image = imageId
                ? `${API_BASE_URL}/api/image/${imageId}`
                : "https://via.placeholder.com/300x240?text=No+Image"

              // Get the first variation's price for display
              const firstVariation = variations[0]
              const price = firstVariation?.item_variation_data?.price_money?.amount
                ? (firstVariation.item_variation_data.price_money.amount / 100).toFixed(2)
                : "0.00"

              const isImageLoaded = loadedImages[id]

              // Determine if the item requires customization (i.e., has modifiers)
              const requiresCustomization = modifierListInfo.length > 0

              return (
                <div
                  key={id}
                  className="w-full flex flex-col bg-white/5 p-4 rounded-lg hover:bg-white/10 transition-colors duration-300"
                >
                  <div className="flex-grow w-full">
                    <Link
                      to="/product"
                      state={{
                        id,
                        image,
                        name,
                        description,
                        variations,
                        modifierListInfo,
                        fullItemData: item_data, // Pass full item data for debugging
                      }}
                      className="cursor-pointer w-full"
                    >
                      {/* Image container with skeleton */}
                      <div className="w-full aspect-square rounded overflow-hidden relative">
                        {!isImageLoaded && <div className="absolute inset-0 bg-gray-700 animate-pulse"></div>}
                        <img
                          className={`w-full h-full object-cover transition-opacity duration-500 ${
                            isImageLoaded ? "opacity-100" : "opacity-0"
                          }`}
                          src={image || "/placeholder.svg"}
                          alt={name}
                          onLoad={() => handleImageLoad(id)}
                          onError={() => {
                            console.log(`Failed to load image for ${name}:`, image)
                          }}
                        />
                      </div>

                      {/* Text Content */}
                      <div className="flex flex-col items-center gap-2 mt-4 w-full">
                        {!isImageLoaded ? (
                          <div className="h-7 w-3/4 bg-gray-700 animate-pulse rounded"></div>
                        ) : (
                          <h3 className="text-white text-xl md:text-2xl font-bold text-center line-clamp-2">{name}</h3>
                        )}

                        <p className="text-white/80 text-sm md:text-base text-center line-clamp-3">{description}</p>

                        {!isImageLoaded ? (
                          <div className="h-6 w-16 bg-gray-700 animate-pulse rounded mt-2"></div>
                        ) : (
                          <div className="text-center">
                            <p className="text-white font-medium text-lg">${price}</p>
                            {variations.length > 1 && (
                              <p className="text-white/60 text-sm">{variations.length} options available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  </div>

                  {/* Conditional Add to Cart / Customize Button */}
                  {requiresCustomization ? (
                    <Link
                      to="/product"
                      state={{
                        id,
                        image,
                        name,
                        description,
                        variations,
                        modifierListInfo,
                        fullItemData: item_data,
                      }}
                      className="w-full bg-orange-400 hover:bg-orange-500 text-black font-semibold py-2 px-4 rounded transition-colors duration-300 mt-4 text-center"
                    >
                      Customize
                    </Link>
                  ) : (
                    <button
                      className="w-full bg-orange-400 hover:bg-orange-500 text-black font-semibold py-2 px-4 rounded transition-colors duration-300 mt-4"
                      onClick={() => handleAddToCartClick(item)}
                    >
                      Add to Cart
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Notification */}
      {showNotification && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-fade-in-up">
          <CheckCircle className="w-6 h-6" />
          <span className="text-lg font-medium">{notificationMessage}</span>
        </div>
      )}
    </div>
  )
}

export default MenuSection
